/*
 * RefundRequests.ts (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file RefundRequests.ts
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import { showSuccessToast } from '@abstract/abstractwebcommon-client/AlertToast/AlertToast';
import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { IAPIEntityResponse } from '@abstract/abstractwebcommon-shared/interfaces/api';
import { IRefundRequest } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/RefundRequest';
import { ISubscription } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/Subscription';
import {
  createRefundRequestService,
  getAllRefundRequestsService,
  approveRefundRequestService,
  rejectRefundRequestService
} from '../Services/RefundRequests';
import { getAllSubscriptionsSuccess as getAllAdminSubscriptionsSuccess } from './Subscriptions/AdminSubscriptions';
import { getSubscriptionsByUserUUIDSuccess } from './Subscriptions/ClientSubscriptions';

const GET_REFUND_REQUESTS_REQUEST = 'refundRequests/get/all/request';
const GET_REFUND_REQUESTS_SUCCESS = 'refundRequests/get/all/success';
const GET_REFUND_REQUESTS_FAILURE = 'refundRequests/get/all/failure';

const CREATE_REFUND_REQUEST_REQUEST = 'refundRequests/create/request';
const CREATE_REFUND_REQUEST_SUCCESS = 'refundRequests/create/success';
const CREATE_REFUND_REQUEST_FAILURE = 'refundRequests/create/failure';

const REVIEW_REFUND_REQUEST_REQUEST = 'refundRequests/review/request';
const REVIEW_REFUND_REQUEST_SUCCESS = 'refundRequests/review/success';
const REVIEW_REFUND_REQUEST_FAILURE = 'refundRequests/review/failure';

const INITIAL_STATE = {
  list: [] as IRefundRequest[],
  totalRecords: 0,
  isFetchingList: false,
  isReviewing: false,
  isCreatingRequest: false
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case GET_REFUND_REQUESTS_REQUEST:
      return { ...state, isFetchingList: true };
    case GET_REFUND_REQUESTS_SUCCESS:
      return {
        ...state,
        isFetchingList: false,
        list: action.payload.refundRequests,
        totalRecords: action.payload.totalRecords
      };
    case GET_REFUND_REQUESTS_FAILURE:
      return { ...state, isFetchingList: false };
    case CREATE_REFUND_REQUEST_REQUEST:
      return { ...state, isCreatingRequest: true };
    case CREATE_REFUND_REQUEST_SUCCESS:
      return { ...state, isCreatingRequest: false };
    case CREATE_REFUND_REQUEST_FAILURE:
      return { ...state, isCreatingRequest: false };
    case REVIEW_REFUND_REQUEST_REQUEST:
      return { ...state, isReviewing: true };
    case REVIEW_REFUND_REQUEST_SUCCESS:
      return { ...state, isReviewing: false };
    case REVIEW_REFUND_REQUEST_FAILURE:
      return { ...state, isReviewing: false };
    default:
      return state;
  }
};

const getRefundRequestsRequest = () => ({
  type: GET_REFUND_REQUESTS_REQUEST
});

export const getRefundRequestsSuccess = (
  refundRequests: IRefundRequest[],
  totalRecords: number
) => ({
  type: GET_REFUND_REQUESTS_SUCCESS,
  payload: { refundRequests, totalRecords }
});

const getRefundRequestsFailure = (error: string) => ({
  type: GET_REFUND_REQUESTS_FAILURE,
  error
});

const createRefundRequestRequest = () => ({
  type: CREATE_REFUND_REQUEST_REQUEST
});

const createRefundRequestSuccess = () => ({
  type: CREATE_REFUND_REQUEST_SUCCESS
});

const createRefundRequestFailure = (error: string) => ({
  type: CREATE_REFUND_REQUEST_FAILURE,
  error
});

const reviewRefundRequestRequest = () => ({
  type: REVIEW_REFUND_REQUEST_REQUEST
});

const reviewRefundRequestSuccess = () => ({
  type: REVIEW_REFUND_REQUEST_SUCCESS
});

const reviewRefundRequestFailure = (error: string) => ({
  type: REVIEW_REFUND_REQUEST_FAILURE,
  error
});

/**
 * Files a refund request for a subscription. Used by the subscription owner.
 * Updates the cached subscription list so the button immediately reflects the
 * pending state, avoiding a flicker before the list is refetched.
 */
export const createRefundRequestAction = (
  subscriptionID: string,
  reason: string,
  subscriptionsList: ISubscription[],
  isAdminCall = false
) => async (dispatch, getState) => {
  const errorMessage: string =
    'Something went wrong, please contact our support team.';

  try {
    dispatch(createRefundRequestRequest());

    const result: IAPIEntityResponse<string> = await asyncErrorHandler(
      createRefundRequestService(subscriptionID, reason)
    );

    if (result.error) {
      handleError({ message: errorMessage });
      dispatch(createRefundRequestFailure(result.error as string));
    } else {
      dispatch(createRefundRequestSuccess());

      // NOTE: Optimistically mark the requested subscription as having a pending request, so its
      // NOTE: button flips to the disabled "Refund requested" state before the list is refetched.
      const updatedSubscriptionList: ISubscription[] = subscriptionsList.map(
        (subscription: ISubscription) => {
          if (subscription._id === subscriptionID) {
            return { ...subscription, pendingRefundRequest: true };
          }
          return subscription;
        }
      );

      // NOTE: Preserve the real paginated total; using the page length here would corrupt the
      // NOTE: paginator (e.g. collapse 25 records to the 10 on screen) until the next fetch.
      const state = getState();
      if (isAdminCall) {
        dispatch(
          getAllAdminSubscriptionsSuccess(
            updatedSubscriptionList,
            state?.adminSubscriptions?.totalRecords ??
              updatedSubscriptionList.length
          )
        );
      } else {
        dispatch(
          getSubscriptionsByUserUUIDSuccess(
            updatedSubscriptionList,
            state?.clientSubscriptions?.totalRecords ??
              updatedSubscriptionList.length
          )
        );
      }

      showSuccessToast(result.message ?? 'Refund request submitted successfully.');
    }
  } catch (error) {
    dispatch(createRefundRequestFailure(error as string));
    handleError({ message: errorMessage });
  }
};

/**
 * Retrieves refund requests with pagination for the admin review queue.
 */
export const getRefundRequestsAction = (
  skip?: number,
  limit?: number,
  searchTerm?: string,
  sort?: { sortField: string; sortOrder: number }
) => async (dispatch) => {
  const errorMessage: string = 'Failed to get refund requests.';

  try {
    dispatch(getRefundRequestsRequest());

    const result: IAPIEntityResponse<IRefundRequest[]> = await asyncErrorHandler(
      getAllRefundRequestsService(skip, limit, searchTerm, sort)
    );

    if (result.error) {
      handleError({ message: errorMessage });
      dispatch(getRefundRequestsFailure(result.error as string));
    } else {
      dispatch(
        getRefundRequestsSuccess(
          ((result as any).refundRequests as IRefundRequest[]) ?? [],
          (result as any).totalRecords ?? 0
        )
      );
    }
  } catch (error) {
    dispatch(getRefundRequestsFailure(error as string));
    handleError({ message: errorMessage });
  }
};

/**
 * Approves a pending refund request (executes the refund) and refreshes the queue.
 */
export const approveRefundRequestAction = (
  refundRequestID: string,
  refreshPayload: {
    skip?: number;
    limit?: number;
    searchTerm?: string;
    sort?: { sortField: string; sortOrder: number };
  }
) => async (dispatch) => {
  const errorMessage: string = 'Failed to approve refund request.';

  try {
    dispatch(reviewRefundRequestRequest());

    const result: IAPIEntityResponse<string> = await asyncErrorHandler(
      approveRefundRequestService(refundRequestID)
    );

    if (result.error) {
      handleError({ message: errorMessage });
      dispatch(reviewRefundRequestFailure(result.error as string));
    } else {
      dispatch(reviewRefundRequestSuccess());
      showSuccessToast(result.message ?? 'Refund request approved.');
      dispatch(
        getRefundRequestsAction(
          refreshPayload.skip,
          refreshPayload.limit,
          refreshPayload.searchTerm,
          refreshPayload.sort
        )
      );
    }
  } catch (error) {
    dispatch(reviewRefundRequestFailure(error as string));
    handleError({ message: errorMessage });
  }
};

/**
 * Rejects a pending refund request and refreshes the queue.
 */
export const rejectRefundRequestAction = (
  refundRequestID: string,
  rejectionReason: string,
  refreshPayload: {
    skip?: number;
    limit?: number;
    searchTerm?: string;
    sort?: { sortField: string; sortOrder: number };
  }
) => async (dispatch) => {
  const errorMessage: string = 'Failed to reject refund request.';

  try {
    dispatch(reviewRefundRequestRequest());

    const result: IAPIEntityResponse<string> = await asyncErrorHandler(
      rejectRefundRequestService(refundRequestID, rejectionReason)
    );

    if (result.error) {
      handleError({ message: errorMessage });
      dispatch(reviewRefundRequestFailure(result.error as string));
    } else {
      dispatch(reviewRefundRequestSuccess());
      showSuccessToast(result.message ?? 'Refund request rejected.');
      dispatch(
        getRefundRequestsAction(
          refreshPayload.skip,
          refreshPayload.limit,
          refreshPayload.searchTerm,
          refreshPayload.sort
        )
      );
    }
  } catch (error) {
    dispatch(reviewRefundRequestFailure(error as string));
    handleError({ message: errorMessage });
  }
};
